import { useEffect, useState } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AlertCircle, CreditCard, ArrowRight, RefreshCw, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionBlockedPage() {
  const navigate = useNavigate();
  const { token, restaurant } = useOutletContext() || {};
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptionStatus = useLatestCallback(async () => {
    try {
      const merchantId = restaurant?.id || localStorage.getItem('backoffice_active_restaurant');
      if (!merchantId) {
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API}/api/admin/merchants/${merchantId}/entitlements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@asterpos.com?subject=Subscription%20Help';
  };

  const handleUpgrade = () => {
    // Navigate to upgrade/pricing page
    navigate(bo('settings?tab=billing'));
  };

  const getStatusBadge = () => {
    const status = subscription?.subscription?.status || 'none';
    const statusConfig = {
      'trialing': { label: 'Trial', variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      'active': { label: 'Active', variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200' },
      'past_due': { label: 'Past Due', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200' },
      'canceled': { label: 'Canceled', variant: 'outline', className: 'bg-gray-50 text-gray-700 border-gray-200' },
      'paused': { label: 'Paused', variant: 'outline', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      'none': { label: 'No Subscription', variant: 'outline', className: 'bg-gray-50 text-gray-500 border-gray-200' }
    };
    const config = statusConfig[status] || statusConfig.none;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  const status = subscription?.subscription?.status || 'none';
  const isTrialExpired = status === 'trialing' && subscription?.subscription?.trial_ends_at && 
    new Date(subscription.subscription.trial_ends_at) < new Date();
  const isPastDue = status === 'past_due';
  const isCanceled = status === 'canceled' || status === 'paused';
  const noSubscription = status === 'none' || !subscription?.enabled;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="subscription-blocked-page">
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader className="text-center pb-4">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-2xl text-red-900">
            {noSubscription && "Subscription Required"}
            {isTrialExpired && "Trial Expired"}
            {isPastDue && "Payment Required"}
            {isCanceled && "Subscription Inactive"}
          </CardTitle>
          <CardDescription className="text-red-700 mt-2">
            {noSubscription && "You need an active subscription to access the Back Office."}
            {isTrialExpired && "Your trial period has ended. Subscribe to continue using Chain29."}
            {isPastDue && "Your payment is past due. Please update your payment method to continue."}
            {isCanceled && "Your subscription has been canceled or paused."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Status Badge */}
          <div className="flex justify-center">
            {getStatusBadge()}
          </div>

          {/* What you're missing */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3">Features you're missing:</h4>
            <ul className="space-y-2">
              {['Sales & Analytics', 'Menu Management', 'Inventory Control', 'Employee Management', 'Reports & Exports'].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-gray-400" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handleUpgrade}
              className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="upgrade-subscription-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {isPastDue ? 'Update Payment Method' : 'Subscribe Now'}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>

            <div className="grid grid-cols-1 gap-3">
              <Button 
                variant="outline"
                onClick={handleContactSupport}
                className="h-11"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Support
              </Button>
              {/* no phone line: the number here was a 555 placeholder */}
            </div>
          </div>

          {/* Subscription Details (if any) */}
          {subscription?.subscription && (
            <div className="text-center text-sm text-gray-500 pt-4 border-t">
              <p>
                Plan: <span className="font-medium">{subscription.subscription.plan_id?.toUpperCase()}</span>
                {subscription.subscription.trial_ends_at && (
                  <> | Trial ends: <span className="font-medium">
                    {new Date(subscription.subscription.trial_ends_at).toLocaleDateString()}
                  </span></>
                )}
              </p>
            </div>
          )}

          {/* Back to Login */}
          <div className="text-center">
            <button
              onClick={() => {
                localStorage.removeItem('backoffice_token');
                navigate(bo());
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Sign out and switch accounts
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
